import { Info, Play } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Card } from '@/components/ui/card'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  etaLabel,
  formatDurationMs,
  formatRelativeTime,
  statusAccent,
} from '@/features/documents/extraction-run-format'
import type { ExtractionRun } from '@/services/api/cases'
import { cn } from '@/utils/cn'

function TinyStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border bg-background/70 px-2 py-1">
      <div className="text-3xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-2xs font-mono text-foreground tabular-nums">{value}</div>
    </div>
  )
}

function CountBadge({
  label,
  count,
  tone,
}: {
  label: string
  count: number
  tone: 'running' | 'pending' | 'failed'
}) {
  const toneClass = {
    running: 'border-warning/20 bg-warning/10 text-warning',
    pending: 'border-border bg-muted/40 text-muted-foreground',
    failed: 'border-destructive/20 bg-destructive/10 text-destructive',
  }[tone]
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-md border px-2 py-1 text-3xs',
        toneClass
      )}
    >
      <span className="font-semibold tabular-nums">{count}</span>
      <span>{label}</span>
    </span>
  )
}

function InstructionsPopover({ instructions }: { instructions: string }) {
  const { t } = useTranslation()
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={e => e.stopPropagation()}
          // Phone: a 32 px tap target that still takes only 20 px of the title line
          // (negative margin).
          className="inline-flex size-5 items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/40 max-md:-m-1.5 max-md:size-8 max-md:shrink-0"
          aria-label={t('extraction.instructions')}
        >
          <Info className="size-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80 text-xs max-md:max-h-[60dvh] max-md:overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="space-y-1">
          <div className="text-3xs uppercase tracking-wider text-muted-foreground">
            {t('extraction.instructions')}
          </div>
          <div className="whitespace-pre-wrap text-foreground">{instructions}</div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

export function ExtractionRunCard({
  run,
  selected,
  onClick,
}: {
  run: ExtractionRun
  selected?: boolean
  onClick?: () => void
}) {
  const { t } = useTranslation()
  const accent = statusAccent(run.status)
  const isRunning = run.status === 'running'
  const elapsedLabel = formatDurationMs(run.elapsed_ms)
  const eta = isRunning ? etaLabel(run) : null
  const hasSchema = !!run.schema_name
  const title = hasSchema ? run.schema_name : t('extraction.noSchema')
  const allFailed =
    !isRunning &&
    run.total_documents > 0 &&
    run.completed_documents === 0 &&
    run.failed_documents > 0
  const activeLine =
    isRunning && run.active_step_name
      ? run.active_document_name
        ? `${run.active_step_name} on ${run.active_document_name.split('/').pop()}`
        : run.active_step_name
      : null

  return (
    <Card
      asChild
      interactive
      selected={selected}
      className="group relative overflow-hidden pl-5 pr-4 py-3"
    >
      {/* Rendered as a div (not a <button>) because the header embeds an
          Instructions popover trigger — a <button> nested in a <button> is
          invalid HTML. Keyboard support is wired manually below. */}
      <div
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onClick?.()
          }
        }}
        className="w-full text-left"
      >
        <span
          aria-hidden
          className={cn('absolute left-0 top-0 bottom-0 w-1', accent, isRunning && 'animate-pulse')}
        />

        <div className="flex items-center gap-3 max-md:flex-wrap max-md:gap-y-1">
          {/* Phone: the title line never breaks into separate items — a long schema
              name wraps as text between the run id and the version / info button. */}
          <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap max-md:basis-full max-md:flex-nowrap">
            <span
              className="text-3xs font-mono text-muted-foreground/70 tabular-nums max-md:shrink-0"
              title={run.id}
            >
              #{run.id.slice(0, 8)}
            </span>
            <span
              className={cn(
                'text-sm font-medium truncate max-md:min-w-0 max-md:whitespace-normal max-md:break-words',
                hasSchema ? 'text-foreground' : 'text-muted-foreground italic'
              )}
            >
              {title}
            </span>
            {run.schema_version && (
              <span className="text-3xs font-mono uppercase tracking-wider text-muted-foreground max-md:shrink-0">
                {run.schema_version}
              </span>
            )}
            {run.instructions && <InstructionsPopover instructions={run.instructions} />}
          </div>

          {!isRunning && (
            <span className="text-xs text-muted-foreground tabular-nums shrink-0">
              {allFailed ? (
                <>
                  {t('extraction.docs', { count: run.total_documents })}
                  <span className="text-border mx-1.5">·</span>
                  <span className="text-destructive/80">{t('extraction.allFailed')}</span>
                </>
              ) : (
                <>
                  {t('extraction.docs', { count: run.total_documents })}
                  {run.failed_documents > 0 && (
                    <>
                      <span className="text-border mx-1.5">·</span>
                      <span className="text-destructive/80">
                        {run.failed_documents} {t('extraction.failedDocs')}
                      </span>
                    </>
                  )}
                </>
              )}
            </span>
          )}

          <span className="text-xs text-muted-foreground tabular-nums shrink-0">
            {!isRunning && elapsedLabel !== '—' && (
              <>
                {elapsedLabel}
                <span className="text-border mx-1.5">·</span>
              </>
            )}
            {formatRelativeTime(run.created_at)}
          </span>
        </div>

        {isRunning && (
          <div className="mt-2 space-y-1.5">
            {(run.running_documents > 0 ||
              run.pending_documents > 0 ||
              run.failed_documents > 0) && (
              <div className="flex items-center gap-1.5 flex-wrap text-3xs">
                {run.running_documents > 0 && (
                  <CountBadge label="running" count={run.running_documents} tone="running" />
                )}
                {run.pending_documents > 0 && (
                  <CountBadge label="pending" count={run.pending_documents} tone="pending" />
                )}
                {run.failed_documents > 0 && (
                  <CountBadge label="failed" count={run.failed_documents} tone="failed" />
                )}
              </div>
            )}
            {activeLine && (
              <div className="flex items-center gap-1.5 text-2xs text-warning">
                <Play className="size-3 animate-pulse shrink-0" />
                <span className="truncate">{activeLine}</span>
              </div>
            )}
            <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
              <div
                className="h-full rounded-full bg-primary transition-all duration-500"
                style={{ width: `${run.progress_percent}%` }}
              />
            </div>
            <div className="flex items-center justify-between text-xs text-muted-foreground max-md:flex-col max-md:items-stretch max-md:gap-1.5">
              <span>
                {run.completed_documents + run.failed_documents}/{run.total_documents}{' '}
                {t('extraction.processed')}
              </span>
              <div className="grid grid-flow-col gap-1.5 text-right max-md:grid-flow-row max-md:grid-cols-3">
                <TinyStat label="elapsed" value={elapsedLabel} />
                <TinyStat label="eta" value={eta || '—'} />
                <TinyStat label="progress" value={`${run.progress_percent}%`} />
              </div>
            </div>
          </div>
        )}
      </div>
    </Card>
  )
}
